import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { CalendarIcon, Send, Users, User, Shield, Building2, Globe } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useFormularioDistribuicao } from "@/hooks/useFormularioDistribuicao";
import { useUsers } from "@/hooks/useUsers";
import { useGroups } from "@/hooks/useGroups";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

interface FormularioDistribuicaoDialogProps {
  formularioId: string;
  formularioTitulo: string;
  open: boolean;
  onClose: () => void;
}

export function FormularioDistribuicaoDialog({
  formularioId,
  formularioTitulo,
  open,
  onClose,
}: FormularioDistribuicaoDialogProps) {
  const [tipoDestinatario, setTipoDestinatario] = useState<'usuario' | 'grupo' | 'role' | 'client' | 'todos'>('usuario');
  const [destinatarioId, setDestinatarioId] = useState<string>('');
  const [destinatarioRole, setDestinatarioRole] = useState<string>('');
  const [obrigatorio, setObrigatorio] = useState(false);
  const [dataLimite, setDataLimite] = useState<Date | undefined>();
  const [mensagem, setMensagem] = useState('');

  const { distribuirFormulario, isDistribuindo } = useFormularioDistribuicao(formularioId);
  const { users } = useUsers();
  const { groups } = useGroups();
  const { profile } = useAuth();

  const isGlobalAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';

  const rolesDisponiveis = isGlobalAdmin 
    ? [
        { value: 'user', label: 'Usuário' },
        { value: 'technician', label: 'Técnico' },
        { value: 'client_admin', label: 'Administrador do Cliente' },
        { value: 'admin', label: 'Administrador' },
      ]
    : [
        { value: 'user', label: 'Usuário' },
        { value: 'technician', label: 'Técnico' },
        { value: 'client_admin', label: 'Administrador do Cliente' },
      ];

  const handleSubmit = async () => {
    try {
      await distribuirFormulario({
        formularioId,
        tipoDestinatario,
        destinatarioId: tipoDestinatario === 'usuario' || tipoDestinatario === 'grupo' ? destinatarioId : undefined,
        destinatarioRole: tipoDestinatario === 'role' ? destinatarioRole : undefined,
        obrigatorio,
        dataLimite,
        mensagemPersonalizada: mensagem || undefined,
      });
      onClose();
      resetForm();
    } catch (error) {
      console.error('Erro ao distribuir:', error);
    }
  };

  const resetForm = () => {
    setTipoDestinatario('usuario');
    setDestinatarioId('');
    setDestinatarioRole('');
    setObrigatorio(false);
    setDataLimite(undefined);
    setMensagem('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Distribuir Formulário</DialogTitle>
          <DialogDescription>
            Envie o formulário "{formularioTitulo}" para usuários, grupos ou funções específicas
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Tipo de Destinatário */}
          <div className="space-y-2">
            <Label>Para quem enviar?</Label>
            <Select value={tipoDestinatario} onValueChange={(v: any) => setTipoDestinatario(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="usuario">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>Usuário Específico</span>
                  </div>
                </SelectItem>
                <SelectItem value="grupo">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>Grupo Específico</span>
                  </div>
                </SelectItem>
                <SelectItem value="role">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    <span>Por Função (Role)</span>
                  </div>
                </SelectItem>
                <SelectItem value="client">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    <span>Todos do Cliente</span>
                  </div>
                </SelectItem>
                <SelectItem value="todos">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    <span>Todos os Usuários</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Seleção de Usuário */}
          {tipoDestinatario === 'usuario' && (
            <div className="space-y-2">
              <Label>Selecione o Usuário</Label>
              <Select value={destinatarioId} onValueChange={setDestinatarioId}>
                <SelectTrigger>
                  <SelectValue placeholder="Escolha um usuário" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Seleção de Grupo */}
          {tipoDestinatario === 'grupo' && (
            <div className="space-y-2">
              <Label>Selecione o Grupo</Label>
              <Select value={destinatarioId} onValueChange={setDestinatarioId}>
                <SelectTrigger>
                  <SelectValue placeholder="Escolha um grupo" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name} {group.user_count && `(${group.user_count} membros)`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Seleção de Role */}
          {tipoDestinatario === 'role' && (
            <div className="space-y-2">
              <Label>Selecione a Função</Label>
              <Select value={destinatarioRole} onValueChange={setDestinatarioRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Escolha uma função" />
                </SelectTrigger>
                <SelectContent>
                  {rolesDisponiveis.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Obrigatório */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="obrigatorio"
              checked={obrigatorio}
              onCheckedChange={(checked) => setObrigatorio(checked as boolean)}
            />
            <Label htmlFor="obrigatorio" className="cursor-pointer">
              Resposta Obrigatória
            </Label>
          </div>

          {/* Data Limite */}
          <div className="space-y-2">
            <Label>Data Limite (Opcional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dataLimite && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dataLimite ? format(dataLimite, "PPP", { locale: ptBR }) : "Selecione uma data"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dataLimite}
                  onSelect={setDataLimite}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Mensagem Personalizada */}
          <div className="space-y-2">
            <Label>Mensagem Personalizada (Opcional)</Label>
            <Textarea
              placeholder="Adicione uma mensagem para os destinatários..."
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isDistribuindo || (tipoDestinatario === 'usuario' && !destinatarioId) || (tipoDestinatario === 'grupo' && !destinatarioId) || (tipoDestinatario === 'role' && !destinatarioRole)}
          >
            <Send className="h-4 w-4 mr-2" />
            {isDistribuindo ? 'Enviando...' : 'Distribuir Formulário'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
